import { AgRendererComponent } from 'ag-grid-angular';
import { Component } from '@angular/core';

import { ICellRendererParams } from 'ag-grid-community';
import { Primitive } from '@geniuspos/ui-shared';
import { flatten, get, isArray, isEmpty, isFunction, isNil, set } from 'lodash';

import { APIActions } from '@store/actions';
import { IBaseEntityType, IFilterQuery } from '@shared/types';
import { isObjectIdValid, promiseMap } from '@shared/utils';
import { StateService } from '@app/shared/services';

// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xvi-buffered-reference-renderer',
  templateUrl: './buffered-reference-renderer.component.html',
  styleUrls: ['./buffered-reference-renderer.component.scss'],
})
export class XVIBufferedReferenceRenderer implements AgRendererComponent {
  public value?: Primitive;
  public isBusy: boolean = false;

  public constructor(
    protected readonly apiActions: APIActions,
    protected readonly stateService: StateService,
  ) {}

  public async agInit(rendererParams: ICellRendererParams): Promise<void> {
    const existingValue = this.getExistingValue(rendererParams);
    if (!isNil(existingValue)) {
      this.value = existingValue;

      return;
    }

    const currentDocEntityId = this.getDataId(rendererParams);

    const getEntityIdsToFetch =
      rendererParams?.colDef?.cellRendererParams?.getEntityIdsToFetch;
    const data = rendererParams?.data ?? {};
    const entityIdsToFetch = getEntityIdsToFetch
      ? getEntityIdsToFetch(data)
      : currentDocEntityId;
    const {
      entityType,
      fieldPath,
      select,
      valueMapper,
      includeEntities,
      itemCaptionPath,
      userQuery = {},
    } = get(rendererParams, ['colDef', 'cellRendererParams'], {} as any);

    if (!isEmpty(entityIdsToFetch)) {
      const parameters = {
        entityType,
        fieldPath,
      };

      const filterQuery: IFilterQuery = {
        select,
        ...userQuery,
      };

      if (includeEntities) {
        set(filterQuery, 'include_entities', includeEntities);
      }

      this.isBusy = true;
      let receivedDocs: IBaseEntityType[] = !isArray(entityIdsToFetch)
        ? await this.fetchDocuments(
            entityType,
            entityIdsToFetch,
            filterQuery,
            parameters,
          )
        : await promiseMap(
            entityIdsToFetch,
            (entityId: string): Promise<IBaseEntityType[]> => {
              return this.fetchDocuments(
                entityType,
                entityId,
                filterQuery,
                parameters,
              ).catch((err) => err);
            },
          );

      receivedDocs = isArray(receivedDocs)
        ? flatten(receivedDocs)
        : receivedDocs;

      await this.applyValueMapper(
        receivedDocs,
        rendererParams,
        valueMapper,
        itemCaptionPath,
      );
    }
  }

  public async applyValueMapper(
    receivedDocs: IBaseEntityType | string | number,
    rendererParams: ICellRendererParams,
    valueMapper: Function,
    itemCaptionPath: string[],
  ): Promise<void> {
    const valueToSet =
      itemCaptionPath && isObjectIdValid(receivedDocs)
        ? this.stateService.getBySelector([...itemCaptionPath, receivedDocs])
        : receivedDocs;
    this.isBusy = false;
    this.value = isFunction(valueMapper)
      ? valueMapper(valueToSet, rendererParams)
      : valueToSet;
  }

  public refresh(): boolean {
    return false;
  }

  public fetchDocuments(
    entityType,
    currentDocEntityId: string,
    filterQuery: IFilterQuery = {},
    bufferedRendererParams,
  ): Promise<IBaseEntityType[]> {
    const query = {
      ...filterQuery,
      filter: `${bufferedRendererParams.fieldPath} eq in(${currentDocEntityId})`,
    };

    return this.apiActions.fetchList(entityType, query, {
      noLoader: true,
    });
  }

  public getDataId(rendererParams: ICellRendererParams): string {
    const valueFieldPath = get(
      rendererParams,
      'colDef.cellRendererParams.valueFieldPath',
    );

    return get(rendererParams, `data.${valueFieldPath}`, null);
  }

  private getExistingValue(
    rendererParams: ICellRendererParams,
  ): Primitive | undefined {
    const getter = get(
      rendererParams,
      'colDef.cellRendererParams.existingValueGetter',
    );
    if (!isFunction(getter)) return;

    const data = get(rendererParams, 'data', {});

    return getter(data);
  }
}
